import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAdmin } from '../contexts/AdminContext';
import { ADMIN_CONFIG } from '../config/adminConfig';

// Helper function to check if we're in admin mode
const isInAdminMode = () => {
  const isAdminMode = process.env.REACT_APP_ADMIN_MODE === 'true';
  const isAdminSubdomain = window.location.hostname.startsWith('admin.');
  return isAdminMode || isAdminSubdomain;
};

export const useAdminNavigationProtection = () => {
  const { currentUser } = useAuth();
  const { isAdmin } = useAdmin();
  const isProtectionActive = useRef(false);

  useEffect(() => {
    // Only activate protection for authenticated admin users
    if (!currentUser || !isAdmin) {
      isProtectionActive.current = false;
      return;
    }

    isProtectionActive.current = true;
    const inAdminMode = isInAdminMode();

    const handlePopState = (event: PopStateEvent) => {
      if (!isProtectionActive.current) return;

      const currentPath = window.location.pathname;
      
      // Check if trying to navigate to non-admin pages or login pages
      const loginPath = inAdminMode ? '/login' : '/admin/login';
      const signupPath = inAdminMode ? '/signup' : '/admin/signup';
      const adminPath = inAdminMode ? '/' : '/admin';
      const dashboardPath = '/admin/control-panel-7x9k2m';
      
      // Allow all admin routes that start with dashboardPath
      // Also allow admin routes like /templates, /automation-template-editor, etc.
      const isAdminRoute = currentPath.startsWith(dashboardPath) ||
                          currentPath.startsWith('/admin/') ||
                          currentPath === '/templates' ||
                          currentPath.startsWith('/templates/') ||
                          currentPath === '/automation-template-editor' ||
                          currentPath === '/user-contacts' ||
                          currentPath.startsWith('/user-contacts/') ||
                          currentPath === '/user-details' ||
                          currentPath.startsWith('/user-details/');
      
      if (currentPath === loginPath || 
          currentPath === signupPath ||
          (currentPath === adminPath && !inAdminMode) ||
          (!isAdminRoute && currentPath.startsWith('/admin/'))) {
        
        // Only redirect if it's clearly not an admin route
        event.preventDefault();
        window.history.pushState({ admin: true, page: 'dashboard', timestamp: Date.now() }, '', ADMIN_CONFIG.ADMIN_DASHBOARD_URL);
        window.location.href = ADMIN_CONFIG.ADMIN_DASHBOARD_URL;
        return;
      }
    };

    // Handle beforeunload to prevent accidental navigation away from admin
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isProtectionActive.current) return;
      
      // Check if we're on an admin route
      const currentPath = window.location.pathname;
      const dashboardPath = '/admin/control-panel-7x9k2m';
      const isAdminRoute = currentPath.startsWith(dashboardPath) ||
                          currentPath.startsWith('/admin/') ||
                          currentPath === '/templates' ||
                          currentPath.startsWith('/templates/') ||
                          currentPath === '/automation-template-editor' ||
                          currentPath === '/user-contacts' ||
                          currentPath.startsWith('/user-contacts/') ||
                          currentPath === '/user-details' ||
                          currentPath.startsWith('/user-details/');
      
      // Only prevent navigation if it's clearly leaving admin routes
      if (!isAdminRoute && event.target === window) {
        event.preventDefault();
        event.returnValue = 'Are you sure you want to leave the admin dashboard?';
        return 'Are you sure you want to leave the admin dashboard?';
      }
    };

    // Add event listeners
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup function
    return () => {
      isProtectionActive.current = false;
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentUser, isAdmin]);

  // Return the protection status for debugging
  return { isProtectionActive: isProtectionActive.current };
};
